import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable } from 'rxjs';

import { BASE_URL } from '../../core/global/config';
import { Read, ReadResult } from '../models/read';
import { EntityType } from '../models/entity-type.enum';
import {
  AvionBusinessEntity,
  ExecuteQueryResult,
  ExecutionResult,
  Individual,
  ListResult,
  SystemCheckSumsResult,
} from '../models/common';
import { Save, SaveList, SaveListResult, SaveResult } from '../models/save';
import { AttachmentResult } from '../models/attachment-result';

@Injectable({
  providedIn: 'root',
})
export class CommonApiService {
  private baseUrl = inject(BASE_URL);
  private http = inject(HttpClient);

  public readIndividual(data: Read): Observable<ReadResult<Individual>> {
    return this.http.post(this.baseUrl + '/api/aegis/Read', data) as Observable<ReadResult<Individual>>;
  }

  // `context` carries per-request flags such as SUPPRESS_ERROR_TOAST; optional, so existing
  // callers keep the default interactive behaviour.
  public read<T>(entityId: string, entityType: EntityType, displayId?: number, context?: HttpContext): Observable<ReadResult<T>> {
    const requestData: Read = {} as Read;
    requestData.abe = {
      EntityId: entityId,
      EntityTypeId: entityType
    };

    if (displayId) {
      requestData.abe.DisplayId = displayId;
    }

    return this.http.post(this.baseUrl + '/api/aegis/Read', requestData, { context }) as Observable<ReadResult<T>>;
  }

  public executeQuery<T>(entityType: EntityType, queryArgs: any): Observable<ExecuteQueryResult<T>> {
    const requestData = {
      queryEntityId: entityType,
      queryArgs,
    }

    return this.http.post(this.baseUrl + '/api/aegis/ExecuteQuery', requestData) as Observable<ExecuteQueryResult<T>>;
  }

  public readAll<T>(abe: AvionBusinessEntity, filters?: any): Observable<ListResult<T>> {
    const requestData = {
      abe,
    } as any;

    if (filters) {
      requestData.filters = filters;
    }

    return this.http.post(this.baseUrl + '/api/aegis/ReadAll', requestData) as Observable<ListResult<T>>;
  }

  public listByOwner<T>(entityType: EntityType, ownerEntityId: string, ownerEntityTypeId: string, filters?: any, context?: HttpContext): Observable<ListResult<T>> {
    const data = {
      entityTypeId: entityType,
      ownerEntityId,
      ownerEntityTypeId,
    } as any;

    if (filters) {
      data.filters = filters
    }

    return this.http.post(this.baseUrl + '/api/aegis/ListByOwner', data, { context }) as Observable<ListResult<T>>;
  }

  public listByOwnerAttachment(ownerEntityId: string, ownerEntityTypeId: string): Observable<AttachmentResult> {
    const data = {
      entityTypeId: EntityType.attachment,
      ownerEntityId,
      ownerEntityTypeId,
    }

    return this.http.post(this.baseUrl + '/api/aegis/ListByOwner', data) as Observable<AttachmentResult>;
  }

  public save<T>(item: T): Observable<SaveResult<T>> {
    const abe: T = {...item}

    const requestData: Save<T> = {
      abe: abe,
    };

    return this.http.post(this.baseUrl + '/api/aegis/Save', requestData) as Observable<SaveResult<T>>
  }

  public saveList<T>(items: T[]): Observable<SaveListResult<T>> {
    const requestData: SaveList<T> = {
      abes: items,
    };

    return this.http.post(this.baseUrl + '/api/aegis/SaveList', requestData) as Observable<SaveListResult<T>>;
  }

  public delete(item: any): Observable<ExecutionResult> {
    const data = {
      abe: item,
    }

    return this.http.post(this.baseUrl + '/api/aegis/Delete', data) as Observable<ExecutionResult>;
  }

  public listByRelationship<T>(entityType: EntityType, owner: AvionBusinessEntity): Observable<ListResult<T>> {
    const data = {
      entityTypeId: entityType,
      relatedEntityTypeId: owner.EntityTypeId,
      relatedEntityId: owner.EntityId,
    };

    return this.http.post(this.baseUrl + '/api/aegis/ListByRelationship', data) as Observable<ListResult<T>>;
  }

  public saveAbeWithRelationship(owner: AvionBusinessEntity, child: AvionBusinessEntity): Observable<SaveResult<any>> {
    const data = {
      abe: child,
      relatedEntityTypeId: owner.EntityTypeId,
      relatedEntityId: owner.EntityId,
    };

    return this.http.post(this.baseUrl + '/api/aegis/SaveByRelationship', data) as Observable<SaveResult<any>>
  }

  public deleteRelationship(
    entityTypeId: string,
    entityId: string,
    relatedEntityTypeId: string,
    relatedEntityId: string
  ): Observable<ExecutionResult> {
    let data = {
      entityTypeId: entityTypeId,
      entityId: entityId,
      relatedEntityTypeId: relatedEntityTypeId,
      relatedEntityId: relatedEntityId,
    };

    return this.http.post(this.baseUrl + '/api/aegis/DeleteRelationship', data) as Observable<ExecutionResult>;
  }

  public getSystemCheckSums(): Observable<SystemCheckSumsResult> {
    return this.http.post(this.baseUrl + '/api/aria/GetSystemChecksums', {}) as Observable<SystemCheckSumsResult>
  }
}
